import React, { useState, useCallback } from 'react';
import { useProgress } from '../context/ProgressContext';
import { Modal, ToastContainer } from '../components';
import type { ToastData } from '../components';
import { exportProgress, importProgress } from '../lib/storage';
import './SettingsPage.css';

export const SettingsPage: React.FC = () => {
  const { progress, updateChildrenNames, resetProgress, refreshProgress, refreshAITrophies } = useProgress();

  const [playerName, setPlayerName] = useState(progress.childrenNames[0] || '');
  const [showResetModal, setShowResetModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = useCallback((message: string, type: ToastData['type']) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleSaveName = () => {
    updateChildrenNames([playerName.trim() || '']);
    addToast('Numele a fost salvat!', 'success');
  };

  const handleExport = async () => {
    try {
      await exportProgress();
      addToast('Progresul a fost exportat!', 'success');
    } catch {
      addToast('Eroare la export', 'error');
    }
  };

  const handleImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      setShowImportModal(true);
    }
    e.target.value = '';
  };

  const handleImportConfirm = async () => {
    if (!importFile) return;

    setImporting(true);
    const result = await importProgress(importFile);
    setImporting(false);
    setShowImportModal(false);
    setImportFile(null);

    if (result.success) {
      refreshProgress();
      await refreshAITrophies();
      addToast('Progresul a fost importat cu succes!', 'success');
    } else {
      addToast(result.error || 'Eroare la import', 'error');
    }
  };

  const handleResetConfirm = () => {
    resetProgress();
    setPlayerName('');
    setShowResetModal(false);
    addToast('Progresul a fost resetat', 'info');
  };

  return (
    <div className="settings-page">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <header className="settings-header">
        <h1>⚙️ Setări</h1>
      </header>

      <section className="settings-section">
        <h2>Personalizare (opțional)</h2>
        <p className="section-description">
          Adaugă-ți numele pentru un salut personalizat. Lasă gol dacă preferi.
        </p>
        <div className="name-input-row">
          <input
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder="Numele tău (opțional)"
          />
          <button className="btn btn-primary" onClick={handleSaveName} type="button">
            Salvează
          </button>
        </div>
      </section>

      <section className="settings-section">
        <h2>Export & Import</h2>
        <p className="section-description">
          Salvează progresul sau restaurează din backup.
        </p>
        <div className="export-import-actions">
          <button className="btn btn-primary" onClick={handleExport} type="button">
            📥 Exportă Progresul
          </button>
          <label className="btn btn-secondary import-btn">
            📤 Importă Progres
            <input
              type="file"
              accept=".json"
              onChange={handleImportFileChange}
              hidden
            />
          </label>
        </div>
      </section>

      <section className="settings-section">
        <h2>Statistici</h2>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-label">Lecții completate</span>
            <span className="stat-value">{progress.lessonsCompleted.length}/30</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Trofee deblocate</span>
            <span className="stat-value">
              {Object.keys(progress.trophiesUnlocked).length}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">High Score Joc</span>
            <span className="stat-value">{progress.gameHighScore}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Început la</span>
            <span className="stat-value">
              {new Date(progress.createdAt).toLocaleDateString('ro-RO')}
            </span>
          </div>
        </div>
      </section>

      <section className="settings-section info-section">
        <h2>💾 Cum se salvează progresul?</h2>
        <div className="info-content">
          <p>
            Progresul tău este salvat în <strong>localStorage</strong> - o memorie 
            specială a browserului care păstrează datele chiar și când închizi pagina.
          </p>
          <p>
            Gândește-te la el ca la un sertar secret pe care doar acest site îl poate deschide. 
            Datele rămân pe calculatorul tău și nu sunt trimise nicăieri.
          </p>
          <p className="info-warning">
            ⚠️ <strong>Atenție:</strong> Dacă ștergi datele browserului sau folosești alt 
            browser/calculator, progresul se va pierde. Folosește <em>Export</em> pentru backup!
          </p>
        </div>
      </section>

      <section className="settings-section danger-zone">
        <h2>Zonă Periculoasă</h2>
        <p className="section-description">
          Această acțiune nu poate fi anulată. Asigură-te că ai exportat progresul înainte.
        </p>
        <button
          className="btn btn-danger"
          onClick={() => setShowResetModal(true)}
          type="button"
        >
          🗑️ Resetează Tot Progresul
        </button>
      </section>

      <Modal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="Resetează Progresul"
        actions={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setShowResetModal(false)}
              type="button"
            >
              Anulează
            </button>
            <button className="btn btn-danger" onClick={handleResetConfirm} type="button">
              Da, Resetează
            </button>
          </>
        }
      >
        <p>
          Ești sigur că vrei să resetezi tot progresul? Această acțiune va șterge:
        </p>
        <ul>
          <li>Toate lecțiile completate</li>
          <li>Toate trofeele deblocate</li>
          <li>High score-ul din joc</li>
        </ul>
        <p>
          <strong>Această acțiune nu poate fi anulată!</strong>
        </p>
      </Modal>

      <Modal
        isOpen={showImportModal}
        onClose={() => {
          setShowImportModal(false);
          setImportFile(null);
        }}
        title="Importă Progres"
        actions={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowImportModal(false);
                setImportFile(null);
              }}
              type="button"
            >
              Anulează
            </button>
            <button
              className="btn btn-primary"
              onClick={handleImportConfirm}
              disabled={importing}
              type="button"
            >
              {importing ? 'Importez...' : 'Importă'}
            </button>
          </>
        }
      >
        <p>
          Fișier selectat: <strong>{importFile?.name}</strong>
        </p>
        <p>
          Importarea va înlocui progresul curent. Ești sigur că vrei să continui?
        </p>
      </Modal>
    </div>
  );
};
